<template lang="pug">
y-dialog(v-model="showDialog" max-width="1000" content-class="round-2xl" persistent scrollable)
  v-card
    v-card-title.pa-0
      div(style="width: 100%")
        v-row.pa-4(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center Consolidate e-Invoice
          v-col(cols="1" align="right")
            yz-btn.ml-n3(icon="mdi-close" icon-only icon-color="neutral-700" @click="closeDialog")
        v-divider

    v-card-text.px-6.pt-4
      //- Tabs
      yz-tabs(v-model="activeTab" @change="onTabChange")
        yz-tab(label="Self-pay")
        yz-tab(label="Panel")

      //- Filter chips
      invoice-filter-row.mt-4(:filters.sync="filters" :payment-type="currentPaymentType" :panel-data.sync="panelData" ref="filterRow")

      //- Description
      .text-sm.neutral600--text.mt-4.mb-2 Select which invoices you would like to consolidate for e-Invoice submission.

      //- Data table
      .data-table
        yz-data-table(:headers="headers" :items="invoices" item-key="id" :loading="isLoading" :items-per-page="pageSize" hide-default-footer)
          template(v-slot:no-data)
            .py-8
              .text-sm.neutral500--text.text-center {{ emptyStateMessage }}

          //- Amount column
          template(v-slot:item.amount="{ item }")
            div.yz-medium-body-sm.neutral700--text {{ item.amount | displayPrice }}

          //- Patient name column
          template(v-slot:item.patient="{ item }")
            div.yz-regular-body-sm.neutral700--text {{ item.patient }}

          //- Invoice number column
          template(v-slot:item.invoice_no="{ item }")
            div.yz-regular-body-sm.neutral700--text Bill {{ item.invoice_no }}

          //- Payment method column
          template(v-slot:item.payment_methods="{ item, index }")
            div(:id="`dialog-pm-${index}`" style="position: relative;")
            div.d-flex.align-center.yz-regular-body-sm
              template(v-for="(method, idx) in getDisplayedPaymentMethods(item.payment_methods)")
                y-chip(:key="idx" :label="method" size="sm" chip-class="pa-1 mr-2" outlined)
              v-tooltip(v-if="item.payment_methods.length > 2" top :attach="`#dialog-pm-${index}`" color="neutral700")
                template(#activator="{ on, attrs }")
                  div(v-bind="attrs" v-on="on")
                    y-chip.cursor-help(v-bind="attrs" v-on="on" :label="`+${item.payment_methods.length - 2} more`" size="sm" chip-class="pa-1" outlined)
                div
                  div(v-for="(method, idx) in item.payment_methods" :key="idx") {{ idx + 1 }}. {{ method }}

          //- Date column
          template(v-slot:item.date="{ item }")
            div.yz-regular-body-sm.neutral700--text {{ item.date | humanizeDate }}

        .d-flex.justify-end.mt-4(v-if="totalCount > pageSize")
          yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="page" arrow @input="onPageChange")

    v-divider.mt-4
    v-card-actions.px-6.py-3
      yz-btn(label="Cancel" text underline text-color="neutral-700" size="lg" @click="closeDialog")
      v-spacer
      yz-btn(label="Confirm" size="lg" @click="onSave")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import InvoiceFilterRow from './filter/InvoiceFilterRow.vue'

export default {
  name: 'CreateConsolidatedEInvoiceDialog',
  components: {
    InvoiceFilterRow
  },
  data: () => ({
    showDialog: true,
    isLoading: false,
    activeTab: 0,
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    invoices: [],
    filters: [],
    panelData: null
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    currentPaymentType() {
      return this.activeTab === 0 ? 'self-pay' : 'panel'
    },
    headers() {
      const amountLabel = this.activeTab === 0 ? 'Self-pay Amount' : 'Panel Amount'
      return [
        { text: amountLabel, value: 'amount', class: 'text-table-header' },
        { text: 'Patient Name', value: 'patient', class: 'text-table-header' },
        { text: 'Invoice Number', value: 'invoice_no', class: 'text-table-header' },
        { text: 'Payment Method', value: 'payment_methods', class: 'text-table-header', sortable: false },
        { text: 'Created Date', value: 'date', class: 'text-table-header no-wrap' },
      ]
    },
    emptyStateMessage() {
      if (this.filters.length === 0) {
        return 'Please apply a date filter to view invoices'
      }
      const hasDateFilter = this.filters.some(f => f.key?.includes('date'))
      const hasPanelFilter = this.filters.some(f => f.key === 'panel')

      if (!hasDateFilter) {
        return 'Please apply a date filter to view invoices'
      }
      if (this.currentPaymentType === 'panel' && !hasPanelFilter) {
        return 'Please select a panel to view invoices'
      }
      return 'No invoices available'
    }
  },
  watch: {
    filters: {
      handler() {
        const hasDateFilter = this.filters.some(f => f.key?.includes('date'))
        const hasPanelFilter = this.filters.some(f => f.key === 'panel')

        // Self-pay: only need date filter
        // Panel: need both date AND panel filter
        const canFetch = this.currentPaymentType === 'self-pay'
          ? hasDateFilter
          : (hasDateFilter && hasPanelFilter)

        if (canFetch) {
          this.currentPage = 1
          this.fetchInvoices()
        } else {
          this.invoices = []
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    
    fetchInvoices() {
      this.isLoading = true

      const params = {
        company: this.company.id,
        page_size: this.pageSize,
        page: this.currentPage,
      }

      // Map date filters to start_date / end_date
      this.filters.forEach(filter => {
        if (filter.key === 'date__gte') params.start_date = filter.value
        else if (filter.key === 'date__lte') params.end_date = filter.value
        else if (filter.key === 'date') {
          params.start_date = filter.value
          params.end_date = filter.value
        } else if (filter.key === 'panel') {
          params.panel = filter.value
        }
      })

      this.$axios.get('/einvoices/eligible_invoices/', { params })
        .then(resp => {
          this.totalCount = resp.data?.count || 0
          if (resp.data?.results) {
            const channel = this.currentPaymentType
            this.invoices = resp.data.results.map(item => ({
              rid: item.rid,
              pid: item.pid,
              amount: (item.transactions || []).filter(t => t.channel === channel).reduce((sum, t) => sum + (t.amount || 0), 0),
              patient: item.customer?.name || '-',
              invoice_no: item.rid,
              payment_methods: (item.payment_methods || []).filter(pm => pm.channel === channel).map(pm => pm.name),
              date: item.record_date
            }))
          } else {
            this.invoices = []
          }
        })
        .catch(() => {
          this.invoices = []
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    getDisplayedPaymentMethods(methods) {
      if (!methods || methods.length === 0) return []
      return methods.slice(0, 2)
    },

    onPageChange() {
      this.fetchInvoices()
    },

    onTabChange() {
      this.currentPage = 1
      this.totalCount = 0
      this.invoices = []
    },

    closeDialog() {
      this.$emit('closed')
    },

    onSave() {
      // Emit confirm event with all displayed invoices, payment method, and date filter
      const paymentMethod = this.activeTab === 0 ? 'self-pay' : 'panel'

      // Reconstruct dateFilter - range filters are spread as individual gte/lte
      const gteFilter = this.filters.find(f => f.key === 'date__gte')
      const lteFilter = this.filters.find(f => f.key === 'date__lte')
      const singleFilter = this.filters.find(f => f.key === 'date')

      let dateFilter
      if (gteFilter && lteFilter) {
        dateFilter = [gteFilter, lteFilter]
      } else if (singleFilter) {
        dateFilter = singleFilter
      }

      this.$emit('confirm', { paymentMethod, dateFilter, panelData: this.panelData })
    }
  }
}
</script>

